import { useFormContext, useWatch } from 'react-hook-form';

import { SuccessPaymentModal } from '@/features/success-payment-modal';

import { useDisclosure } from '@/shared/lib';
import {
  Button,
  DashboardDialog,
  DashboardDialogProps,
  Heading,
  Typography,
} from '@/shared/ui';

import { useExchangeSearchAction } from '../libs/use-search-action.ts';
import { ExchangeFormValues } from '../model/schema';
import { ExchangeSteps } from '../model/steps';
import { useExchangeSteps } from '../model/useExchangeSteps.ts';
import { ExchangeReviewContent } from './exchange-review-content.tsx';
import { twMerge } from 'tailwind-merge';

export const ExchangeReview: React.FC<{ isWidget?: boolean }> = ({ isWidget }) => {
  const { step, setStep } = useExchangeSteps();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { setValue, control } = useFormContext<ExchangeFormValues>();
  const fromCurrency = useWatch({ control, name: 'fromCurrency' });
  const toCurrency = useWatch({ control, name: 'toCurrency' });
  const fromAmount = useWatch({ control, name: 'fromAmount' });
  const toAmount = useWatch({ control, name: 'toAmount' });
  useExchangeSearchAction({ onOpen });
  const handleClose = () => {
    onClose();
    if (step === 'success') {
      setValue('fromAmount', 0);
      setValue('toAmount', 0);
      setValue('fee', 0);
      setValue('rate', 1);
      setStep('review');
    }
  };
  const handleOpenChange = (open: boolean) => {
    if (open && Number(fromAmount) > 0) {
      onOpen();
    } else {
      handleClose();
    }
  };
  const exchangeSteps: Record<ExchangeSteps, DashboardDialogProps> = {
    review: {
      title: 'review details',
      size: 'md',
      content: <ExchangeReviewContent onClose={onClose} className="mt-5" />,
      animationProps: {
        id: 'exchange-review',
        isFirst: true,
      },
    },
    success: {
      hideCloseButton: true,
      disableOutsideClick: true,
      content: (
        <SuccessPaymentModal
          onDone={handleClose}
          isInner
          mainContent={
            <div className="flex flex-col gap-4 text-center">
              <Heading variant="l">all done</Heading>
              <Typography variant="16-r">
                {fromAmount}&#8239;{fromCurrency?.symbol} to {toAmount}&#8239;{toCurrency?.symbol} converted
              </Typography>
            </div>
          }
        />
      ),
      animationProps: {
        id: 'success',
      },
    },
  };

  const isValid = Number(fromAmount) > 0 && Number(toAmount) > 0;
  
  return (
    <DashboardDialog
      open={isOpen}
      onOpenChange={handleOpenChange}
      contentClassName="pt-0"
      height="fixed"
      {...exchangeSteps[step]}
    >
      <Button
        size="d-l"
        variant="contrast-500"
        className={twMerge("w-full mt-1", isWidget && 'mt-3')}
        type="button"
        disabled={!isValid}
      >
        Exchange
      </Button>
    </DashboardDialog>
  );
};
